import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { useYourTitlesProducts } from '../../../api/local-content/your-titles/useYourTitlesProducts';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { ProductSorting } from '../../../../models/Product';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { useYourTitlesCreditSummary } from '../../../api/local-content/your-titles/useYourTitlesCreditsSummary';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router';
import { AppState } from '../../../store/appState';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import NoElementsFound, {
    NO_ELEMENTS_LC_TITLES,
    NO_ELEMENTS_LC_TITLES_FILTERED
} from '../../../shared/components/NoElementsFound';
import FilterUtils from '../../../shared/utils/utils-filter';
import ProductList from '../../../shared/common/ProductList';
import ProductElement from './ProductElement';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { LOCAL_CONTENT_SORT_BY, SortByDropDown } from '../../../shared/common/SortByDropDown';
import DisplayTypeDropDown from '../../../shared/common/DisplayTypeDropDown';
import { YourTitlesHeader } from './YourTitlesHeader';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchYourTitlesProductsAggregationsQuickSearch } from '../../../api/local-content/your-titles/fetchYourTitlesProductsAggregationsQuickSearch';
import { DisplayTypeState } from '../../../store/displayType/state';
import { usePurchaseCredits } from '../../../api/local-content/your-titles/usePurchaseCredits';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchYourTitlesProductsAggregations } from '../../../api/local-content/your-titles/fetchYourTitlesProductsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
const DEFAULT_PAGE_SIZE = 54;

export const YourTitlesPage = () => {
    const { t } = useTranslation();
    const { search } = useLocation();
    const history = useHistory();
    const authentication = useSelector((state: AppState) => state.authentication);
    const currency = useSelector((state: AppState) => state.authentication.spoc.currency);
    const taxName = useSelector((state: AppState) => state.authentication.spoc.taxName);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course, it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display = DISPLAY_TYPE_MINIMAL }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.TITLE_AZ;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    //region FILTERBAR_V2
    const { filters } = useProductFilters();
    const isFiltered = FilterUtils.hasActiveFilter(filters);
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => ['yourTitlesPage', 'aggregation', filters, expandedFilterGroups],
        []
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchYourTitlesProductsAggregations(options),
        []
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        fetchYourTitlesProductsAggregationsQuickSearch,
        []
    );
    //endregion

    const { mutate: purchaseCreditsMutation } = usePurchaseCredits();

    const { products, isLoading: isProductsLoading } = useYourTitlesProducts({
        pagination: { page, pageSize, sortBy },
        filterMap: filter
    });

    const { data: creditsSummary, isLoading: isCreditsSummaryLoading } = useYourTitlesCreditSummary();

    const isLoading = isCreditsSummaryLoading || isProductsLoading;

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function handlePurchaseCredits(message: string) {
        purchaseCreditsMutation(message);
    }

    return (
        <DocumentTitle title={t('components.documentTitle.localContent')}>
            <ContainerInnerWithColumns>
                <LoadingOverlay loading={isLoading} />
                <YourTitlesHeader
                    taxName={taxName}
                    currency={currency}
                    credits={creditsSummary ?? {}}
                    purchaseCredits={handlePurchaseCredits}
                    authentication={authentication}
                    products={products.list}
                />

                <>
                    <div className="filter-bar">
                        <div className="layout-column-33 fl-left">
                            <a onClick={onFilterDisplayToggle} className="fl-left">
                                <i className="material-icons fl-left">list</i>
                                <div style={{ display: 'inline-block' }}> Filter</div>
                            </a>
                        </div>
                        {products.count === 0 && !isLoading ? null : (
                            <div className="layout-column-33 fl-right">
                                <SortByDropDown
                                    onSortChange={setSortBy}
                                    current={sortBy}
                                    sorting={LOCAL_CONTENT_SORT_BY}
                                />
                                <DisplayTypeDropDown
                                    onChange={onDisplayChange}
                                    displayType={display as DisplayTypeState}
                                    excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                                />
                            </div>
                        )}
                        <div className="layout-column-33 fl-left">
                            <Pagination
                                totalItems={products.count}
                                onPageChange={setPage}
                                currentPage={page}
                                pageSize={pageSize}
                            />
                        </div>
                    </div>
                </>

                <div className="content-area-inner flex-ca">
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null}
                    {products.count === 0 && !isLoading ? (
                        <NoElementsFound type={isFiltered ? NO_ELEMENTS_LC_TITLES_FILTERED : NO_ELEMENTS_LC_TITLES} />
                    ) : (
                        <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main')}>
                            {products.count > 0 ? (
                                <div className="ca-main-inner">
                                    <ProductList
                                        products={products.list}
                                        displayType={display ?? DISPLAY_TYPE_MINIMAL}
                                        tileItemComponent={ProductElement}
                                        listItemComponent={ProductElement}
                                        currentPage={page}
                                    />
                                    <Pagination
                                        totalItems={products.count}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                    />
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </ContainerInnerWithColumns>
        </DocumentTitle>
    );
};
